import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

interface DayRevenue {
  date: string
  revenue: number
}

const PERIODS = [
  { label: '7 дн', days: 7 },
  { label: '14 дн', days: 14 },
  { label: '30 дн', days: 30 },
]

function formatRub(v: number) {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v)
}

export default function RevenueChart() {
  const [days, setDays] = useState(14)

  const { data, isLoading } = useQuery<{ data: DayRevenue[] }>({
    queryKey: ['revenue-by-day', days],
    queryFn: () => api.get(`/rentals/stats/revenue-by-day?days=${days}`).then((r) => r.data),
    staleTime: 5 * 60_000,
  })

  const rows = data?.data ?? []
  // Clamp negatives to 0 for bar rendering; the real value is shown in tooltip
  const max = Math.max(...rows.map((r) => Math.max(r.revenue, 0)), 1)
  const total = rows.reduce((s, r) => s + r.revenue, 0)

  // Compare in LOCAL browser date, not UTC, to avoid off-by-one around midnight
  const todayLocalStr = (() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })()

  const formatDay = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('ru', { day: 'numeric', month: 'short' }).replace('.', '')
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-700 text-sm">Выручка по дням</h3>
          {!isLoading && <p className="text-xs text-gray-400 mt-0.5">Итого: {formatRub(total)}</p>}
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                days === p.days ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="h-24 bg-gray-100 rounded-xl animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="h-24 flex items-center justify-center text-gray-400 text-sm">Нет данных</div>
      ) : (
        <div className="flex items-end gap-1 h-24">
          {rows.map((r) => {
            // Clamp negative revenue to 0 for bar height; show ≥4% min so bar is visible
            const barRevenue = Math.max(r.revenue, 0)
            const heightPct = max > 0 ? (barRevenue / max) * 100 : 0
            const isToday = r.date === todayLocalStr
            return (
              <div key={r.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                {/* Tooltip */}
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                  {formatDay(r.date)}: {formatRub(r.revenue)}
                </div>
                <div className="w-full flex items-end" style={{ height: '80px' }}>
                  <div
                    className={`w-full rounded-t-sm transition-all ${
                      r.revenue < 0
                        ? 'bg-red-200'
                        : isToday
                        ? 'bg-brand-500'
                        : r.revenue > 0
                        ? 'bg-brand-200'
                        : 'bg-gray-100'
                    }`}
                    style={{ height: `${Math.max(heightPct, barRevenue > 0 ? 4 : 2)}%` }}
                  />
                </div>
                {/* X-axis label — show every N-th to avoid crowding */}
                {(days <= 14 || rows.indexOf(r) % Math.ceil(days / 10) === 0 || isToday) && (
                  <span className={`text-[10px] leading-none ${isToday ? 'text-brand-600 font-semibold' : 'text-gray-400'}`}>
                    {new Date(r.date + 'T00:00:00').getDate()}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
